// src/pages/academy/AcademyParentWithQuestionsForm.jsx
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Plus,
  Trash2,
  Loader2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  FileText,
  Headphones,
  Video,
} from "lucide-react";
import { academyContentAPI, getApiError } from "../../services/academyService";
import { ACADEMY_ROOT, DIFFICULTY_OPTIONS } from "./academyConstants";
import QuestionTextEditor from "./components/QuestionTextEditor";
import TranslationField from "../../components/TranslationField";

// ============================================
// Rich text helpers
// الـ editor بيرجّع "<p></p>" لما يكون فاضي
// ============================================
const plain = (html) =>
  (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
const isRichEmpty = (html) => plain(html) === "";
const cleanRich = (html) => (isRichEmpty(html) ? "" : html);

// كل اختيار له key ثابت عشان الـ editors ما تخلطش المحتوى لما نحذف اختيار
let optUid = 0;
const makeOptions = (texts) => texts.map((text) => ({ key: ++optUid, text }));

// wrapper بيقفل الـ editor (لو الـ editor نفسه بيدعم disabled هيشتغل الاتنين)
function RichField({ disabled, ...props }) {
  return (
    <div className={disabled ? "pointer-events-none opacity-60" : ""} aria-disabled={disabled}>
      <QuestionTextEditor {...props} disabled={disabled} />
    </div>
  );
}

// ============================================
// إعدادات كل نوع (reading / listening / speaking)
// type: "rich" = QuestionTextEditor
// type: "textarea" = textarea عادي (للترجمة)
// ============================================
const CONFIG = {
  reading: {
    label: "قراءة",
    icon: FileText,
    parentKind: "reading-passage",
    childKind: "reading-question",
    fields: [
      { name: "title", label: "العنوان", type: "text", required: true },
      { name: "passage_text", label: "نص القطعة", type: "rich", required: true, minHeight: "160px" },
      { name: "translation", label: "ترجمة نص القطعة (اختياري)", type: "textarea", required: false },
      { name: "source", label: "المصدر (اختياري)", type: "text", required: false },
    ],
    imageField: { name: "passage_image", label: "صورة القطعة (اختياري)" },
    fileField: null,
  },
  listening: {
    label: "استماع",
    icon: Headphones,
    parentKind: "listening-audio",
    childKind: "listening-question",
    fields: [
      { name: "title", label: "العنوان", type: "text", required: true },
      { name: "transcript", label: "النص المكتوب (اختياري)", type: "rich", required: false, minHeight: "120px" },
      { name: "duration", label: "المدة بالثواني (اختياري)", type: "number", required: false },
    ],
    imageField: { name: "audio_image", label: "صورة مصاحبة (اختياري)" },
    fileField: { name: "audio_file", label: "ملف الصوت", required: true, accept: "audio/*" },
  },
  speaking: {
    label: "تحدث",
    icon: Video,
    parentKind: "speaking-video",
    childKind: "speaking-question",
    fields: [
      { name: "title", label: "العنوان", type: "text", required: true },
      { name: "description", label: "الوصف (اختياري)", type: "rich", required: false, minHeight: "120px" },
      { name: "duration", label: "المدة بالثواني (اختياري)", type: "number", required: false },
    ],
    imageField: { name: "video_image", label: "صورة مصاحبة (اختياري)" },
    fileField: { name: "video_file", label: "ملف الفيديو", required: true, accept: "video/*" },
  },
};

const emptyQuestion = () => ({
  key: crypto.randomUUID(),
  question_text: "",
  translation: "",
  options: makeOptions(["", "", "", ""]), // [{ key, text }]
  correctIndex: 0,
  explanation: "",
  english_explanation: "",
  difficulty: "MEDIUM",
  points: 1,
  question_image: null,
  // حالة الإرسال لكل سؤال لوحده
  status: "idle", // idle | saving | done | error
  error: "",
  savedId: null,
});

const inputCls =
  "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50";

export default function AcademyParentWithQuestionsForm() {
  const navigate = useNavigate();
  const { skillId, contentType } = useParams();
  const cfg = CONFIG[contentType];
  const backPath = `${ACADEMY_ROOT}/skills/${skillId}`;

  const [parent, setParent] = useState(() => {
    const base = {};
    cfg?.fields.forEach((f) => (base[f.name] = ""));
    return base;
  });
  const [parentImage, setParentImage] = useState(null);
  const [parentFile, setParentFile] = useState(null);

  const [questions, setQuestions] = useState(() => [emptyQuestion()]);

  const [saving, setSaving] = useState(false);
  const [parentSaved, setParentSaved] = useState(null); // { id }
  const [topError, setTopError] = useState("");

  if (!cfg) {
    return (
      <div className="p-6 text-center text-red-600">نوع المحتوى غير معروف: {contentType}</div>
    );
  }

  // ============================================
  // Parent fields
  // ============================================
  const setParentField = (name, value) =>
    setParent((prev) => ({ ...prev, [name]: value }));

  // ============================================
  // Questions
  // ============================================
  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion()]);

  const removeQuestion = (key) =>
    setQuestions((prev) => (prev.length > 1 ? prev.filter((q) => q.key !== key) : prev));

  const updateQuestion = (key, patch) =>
    setQuestions((prev) => prev.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const updateChoice = (key, idx, value) =>
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.key !== key) return q;
        return {
          ...q,
          options: q.options.map((o, i) => (i === idx ? { ...o, text: value } : o)),
        };
      })
    );

  const addChoice = (key) =>
    setQuestions((prev) =>
      prev.map((q) =>
        q.key === key && q.options.length < 4
          ? { ...q, options: [...q.options, ...makeOptions([""])] }
          : q
      )
    );

  const removeChoice = (key, idx) =>
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.key !== key || q.options.length <= 2) return q;
        const options = q.options.filter((_, i) => i !== idx);
        let correctIndex = q.correctIndex;
        if (correctIndex === idx) correctIndex = 0;
        else if (correctIndex > idx) correctIndex -= 1;
        return { ...q, options, correctIndex };
      })
    );

  // ============================================
  // Validation
  // ============================================
  const validateParent = () => {
    for (const f of cfg.fields) {
      if (!f.required) continue;
      const v = parent[f.name];
      const empty = f.type === "rich" ? isRichEmpty(v) : !v?.toString().trim();
      if (empty) return `الحقل "${f.label}" مطلوب`;
    }
    if (cfg.fileField?.required && !parentFile) {
      return `الحقل "${cfg.fileField.label}" مطلوب`;
    }
    return "";
  };

  const validateQuestion = (q) => {
    if (isRichEmpty(q.question_text)) return "نص السؤال مطلوب";
    const filled = q.options.map((o) => plain(o.text)).filter(Boolean);
    if (filled.length < 2) return "لازم على الأقل خيارين";
    if (new Set(filled).size !== filled.length) return "الخيارات لازم تكون مختلفة عن بعض";
    if (isRichEmpty(q.options[q.correctIndex]?.text)) return "لازم تحدد الإجابة الصحيحة";
    return "";
  };

  // ============================================
  // Build FormData helpers
  // ============================================
  const buildParentFormData = () => {
    const fd = new FormData();
    fd.append("academy_skill", skillId);
    cfg.fields.forEach((f) => {
      const raw = parent[f.name];
      const value = f.type === "rich" ? cleanRich(raw) : raw;
      if (value !== "" && value != null) fd.append(f.name, value);
    });
    if (parentImage) fd.append(cfg.imageField.name, parentImage);
    if (parentFile) fd.append(cfg.fileField.name, parentFile);
    return fd;
  };

  const buildQuestionFormData = (q, parentId) => {
    const fd = new FormData();
    fd.append("parent_id", parentId);
    fd.append("question_text", q.question_text);
    if (q.translation?.trim()) fd.append("translation", q.translation);
    q.options
      .map((o) => o.text)
      .filter((t) => !isRichEmpty(t))
      .forEach((t) => fd.append("options", t));
    fd.append("correct_answer", q.options[q.correctIndex].text);
    const explanation = cleanRich(q.explanation);
    const englishExplanation = cleanRich(q.english_explanation);
    if (explanation) fd.append("explanation", explanation);
    if (englishExplanation) fd.append("english_explanation", englishExplanation);
    fd.append("difficulty", q.difficulty);
    fd.append("points", q.points);
    if (q.question_image) fd.append("question_image", q.question_image);
    return fd;
  };

  // ============================================
  // Submit one question (أول إرسال + إعادة المحاولة)
  // ============================================
  const submitQuestion = async (q, parentId) => {
    const validationError = validateQuestion(q);
    if (validationError) {
      updateQuestion(q.key, { status: "error", error: validationError });
      return false;
    }
    updateQuestion(q.key, { status: "saving", error: "" });
    try {
      const res = await academyContentAPI.create(cfg.childKind, buildQuestionFormData(q, parentId));
      updateQuestion(q.key, { status: "done", savedId: res?.item?.id ?? null });
      return true;
    } catch (err) {
      updateQuestion(q.key, { status: "error", error: getApiError(err) });
      return false;
    }
  };

  // ============================================
  // Submit الكل
  // ============================================
  const handleSubmit = async () => {
    setTopError("");

    const parentError = validateParent();
    if (parentError) {
      setTopError(parentError);
      return;
    }

    setSaving(true);
    try {
      let parentId = parentSaved?.id;

      // لو القطعة/التسجيل/الفيديو لسه ما اتحفظش، احفظه الأول
      if (!parentId) {
        const res = await academyContentAPI.create(cfg.parentKind, buildParentFormData());
        parentId = res?.item?.id;
        setParentSaved({ id: parentId });
      }

      // الأسئلة اللي لسه مش done (لو بنعيد المحاولة بعد فشل جزئي)
      const pending = questions.filter((q) => q.status !== "done");
      const results = [];
      for (const q of pending) {
        results.push(await submitQuestion(q, parentId));
      }

      // لو كل حاجة نجحت، ارجع لصفحة المهارة
      if (results.every(Boolean)) {
        navigate(backPath);
      }
    } catch (err) {
      setTopError(getApiError(err, "تعذر حفظ القطعة"));
    } finally {
      setSaving(false);
    }
  };

  const Icon = cfg.icon;
  const parentLocked = !!parentSaved; // بعد الحفظ مينفعش تتعدل من هنا

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 text-indigo-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">إضافة {cfg.label} + الأسئلة</h1>
      </div>

      {topError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {topError}
        </div>
      )}

      {parentSaved && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          تم حفظ {cfg.label} بنجاح، دلوقتي كمّل حفظ الأسئلة اللي لسه.
        </div>
      )}

      {/* Parent section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-700">بيانات {cfg.label}</h2>

        {cfg.fields.map((f) => (
          <div key={f.name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} {f.required && <span className="text-red-500">*</span>}
            </label>
            {f.type === "rich" ? (
              <RichField
                disabled={parentLocked}
                value={parent[f.name]}
                onChange={(html) => setParentField(f.name, html)}
                minHeight={f.minHeight}
              />
            ) : f.type === "textarea" ? (
              <textarea
                dir="auto"
                rows={4}
                disabled={parentLocked}
                value={parent[f.name]}
                onChange={(e) => setParentField(f.name, e.target.value)}
                className={inputCls}
              />
            ) : (
              <input
                type={f.type}
                min={f.type === "number" ? 0 : undefined}
                disabled={parentLocked}
                value={parent[f.name]}
                onChange={(e) => setParentField(f.name, e.target.value)}
                className={inputCls}
              />
            )}
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {cfg.imageField.label}
          </label>
          <input
            type="file"
            accept="image/*"
            disabled={parentLocked}
            onChange={(e) => setParentImage(e.target.files?.[0] || null)}
            className="text-sm"
          />
        </div>

        {cfg.fileField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {cfg.fileField.label} {cfg.fileField.required && <span className="text-red-500">*</span>}
            </label>
            <input
              type="file"
              accept={cfg.fileField.accept}
              disabled={parentLocked}
              onChange={(e) => setParentFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
          </div>
        )}
      </div>

      {/* Questions section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">الأسئلة ({questions.length})</h2>
          <button
            type="button"
            onClick={addQuestion}
            className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            إضافة سؤال
          </button>
        </div>

        {questions.map((q, qi) => (
          <QuestionCard
            key={q.key}
            index={qi}
            question={q}
            canRemove={questions.length > 1}
            onRemove={() => removeQuestion(q.key)}
            onChange={(patch) => updateQuestion(q.key, patch)}
            onChoiceChange={(idx, val) => updateChoice(q.key, idx, val)}
            onAddChoice={() => addChoice(q.key)}
            onRemoveChoice={(idx) => removeChoice(q.key, idx)}
            onRetry={() => parentSaved && submitQuestion(q, parentSaved.id)}
          />
        ))}
      </div>

      {/* Submit bar */}
      <div className="sticky bottom-0 z-20 -mx-6 bg-white border-t border-gray-200 px-6 py-4 flex justify-end shadow-[0_-2px_10px_rgba(0,0,0,0.04)]">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition-colors"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {parentSaved ? "حفظ باقي الأسئلة" : `حفظ ${cfg.label} + الأسئلة`}
        </button>
      </div>
    </div>
  );
}

// ============================================
// كارت سؤال واحد
// ============================================
function QuestionCard({
  index,
  question: q,
  canRemove,
  onRemove,
  onChange,
  onChoiceChange,
  onAddChoice,
  onRemoveChoice,
  onRetry,
}) {
  const disabled = q.status === "saving" || q.status === "done";

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">سؤال {index + 1}</span>
          <StatusBadge status={q.status} />
        </div>
        <div className="flex items-center gap-2">
          {q.status === "error" && (
            <button
              type="button"
              onClick={onRetry}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              إعادة المحاولة
            </button>
          )}
          {canRemove && !disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {q.error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {q.error}
        </p>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">نص السؤال</label>
        <RichField
          disabled={disabled}
          value={q.question_text}
          onChange={(html) => onChange({ question_text: html })}
        />
      </div>

      <TranslationField
        label="ترجمة نص السؤال (اختياري)"
        disabled={disabled}
        value={q.translation}
        onChange={(v) => onChange({ translation: v })}
      />

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">الاختيارات</label>
        {q.options.map((opt, idx) => (
          <div key={opt.key} className="flex items-start gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange({ correctIndex: idx })}
              className={`flex items-center justify-center w-7 h-7 mt-1.5 rounded-full text-xs font-bold flex-shrink-0 transition-colors ${
                q.correctIndex === idx
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-400 hover:bg-gray-200"
              }`}
              title="اختار كإجابة صحيحة"
            >
              {"ABCD"[idx]}
            </button>
            <div className="flex-1 min-w-0">
              <RichField
                disabled={disabled}
                value={opt.text}
                onChange={(html) => onChoiceChange(idx, html)}
                minHeight="44px"
              />
            </div>
            {q.options.length > 2 && !disabled && (
              <button
                type="button"
                onClick={() => onRemoveChoice(idx)}
                className="p-1.5 mt-1.5 text-gray-400 hover:text-red-600 rounded-lg"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {q.options.length < 4 && !disabled && (
          <button
            type="button"
            onClick={onAddChoice}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            إضافة اختيار
          </button>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">الشرح (اختياري)</label>
          <RichField
            disabled={disabled}
            value={q.explanation}
            onChange={(html) => onChange({ explanation: html })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            الشرح بالإنجليزي (اختياري)
          </label>
          <RichField
            disabled={disabled}
            value={q.english_explanation}
            onChange={(html) => onChange({ english_explanation: html })}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">الصعوبة</label>
          <select
            disabled={disabled}
            value={q.difficulty}
            onChange={(e) => onChange({ difficulty: e.target.value })}
            className={inputCls}
          >
            {DIFFICULTY_OPTIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">النقاط</label>
          <input
            type="number"
            min={1}
            disabled={disabled}
            value={q.points}
            onChange={(e) => onChange({ points: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">صورة (اختياري)</label>
          <input
            type="file"
            accept="image/*"
            disabled={disabled}
            onChange={(e) => onChange({ question_image: e.target.files?.[0] || null })}
            className="text-xs"
          />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "done")
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="w-3 h-3" /> اتحفظ
      </span>
    );
  if (status === "saving")
    return (
      <span className="flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
        <Loader2 className="w-3 h-3 animate-spin" /> بيتحفظ
      </span>
    );
  if (status === "error")
    return (
      <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
        <AlertCircle className="w-3 h-3" /> فشل
      </span>
    );
  return null;
}